export interface ObserverOptionsType {
  errorMargin: number;
  defaultMargin: number;
  percentCompensation: number;
  minTimeVisible: number;
  useExistedInstance?: boolean;
  config: {
    root: Element | null;
    rootMargin: string;
    threshold: number[];
  };
}

interface IViewObserver {
  destroy: () => void;

  observe: (element: Element, callback: (visible: boolean) => void) => void;
  unobserve: (element: Element) => void;
}

/**
 * Build an array of threshold values ranging from 0 to 1
 * @param {Number} steps - number of steps between 0 and 1.0
 * @returns {Number[]} returns array of floats
 */
export const buildThreshold = (steps = 1) => {
  const threshold = Array.from({ length: steps }, (_x, i) => i / steps);

  // Returns an array like [0, ...n, 1]
  return [...threshold, 1];
};

const DEFAULT_ERROR_MARGIN = 0.25;
const DEFAULT_MARGIN = 0.2;

export const DEFAULT_OPTIONS: ObserverOptionsType = {
  // If an element height is similar to the container height,
  // it's very difficult to catch an impression in 250ms.
  // By default, the intersectionRatio of an element should be
  // 1 to be considered seen and so for tall elements we reduce the
  // max intersectionRatio by this value
  errorMargin: 0, // 25%

  // default to 0.2. The max intersectionRatio for a regular element
  // should be 0.8 as being _too_ strict can result in missed events.
  defaultMargin: 0, // 20%

  // The container height range at which to compensate with
  // an error margin
  percentCompensation: 0, // 20%

  // Element must be visible for >= 250ms
  minTimeVisible: 0,

  // Use the instance of the observer already exists, stay singleton
  useExistedInstance: true,

  // The ViewObserver config
  config: {
    root: null,
    rootMargin: '0px',
    threshold: buildThreshold(1 / Math.min(DEFAULT_ERROR_MARGIN, DEFAULT_MARGIN) + 5),
  },
};

let instance: ViewObserver | null = null;

export default class ViewObserver implements IViewObserver {
  options: ObserverOptionsType;
  elements: Map<
    Element,
    {
      callback: (visible: boolean) => void;
      visible: boolean;
      entry: Element;
      timeoutSet?: boolean;
    }
  >;
  timeouts: Map<Element, ReturnType<typeof setTimeout>>;
  observer: IntersectionObserver;

  static Factory = {
    get(options: ObserverOptionsType): ViewObserver {
      return new ViewObserver(options);
    },
  };

  constructor(options = {}) {
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);

    if (isNaN(this.options.minTimeVisible) || this.options.minTimeVisible < 0) {
      this.options.minTimeVisible = DEFAULT_OPTIONS.minTimeVisible;
    }

    this.elements = new Map();
    this.timeouts = new Map();

    this.observer = new IntersectionObserver(this._watchElements, this.options.config);

    this._addEventListeners();
  }

  /**
   * Return the single instance of the IntersectionObserver if one exists already,
   * otherwise create an instance
   * @param {Object} options - ViewObserver instantiation options
   * @return {ViewObserver} return instance of the class
   */
  static get(options: ObserverOptionsType): ViewObserver {
    // If an instance of the observer already exists, return the singleton
    // otherwise instantiate a new one
    if (instance && instance instanceof ViewObserver && options.useExistedInstance) {
      return instance;
    }

    instance = new ViewObserver(options);
    return instance;
  }

  /**
   * Detroy all Maps and event listeners.
   * @returns {void} returns instance
   */
  public destroy(): void {
    // Clear all elements
    this.elements.clear();

    // Clear all lingering timeouts
    this.timeouts.clear();

    // Disconnect the observer, if available
    if (this.observer && typeof this.observer.disconnect === 'function') {
      this.observer.disconnect();
    }

    // Remove all event listeners
    this._removeEventListeners();

    instance = null;
  }

  /**
   * callback for Intersection Observer
   *
   * Loop through all the observed elements and check if visible
   *
   * @param  {Array} [entries] array of [IntersectionObserverEntry],
   */
  private _watchElements = (entries: IntersectionObserverEntry[] = []): void => {
    // If rootBounds does not exist, it will default to the height and width
    // of the viewport
    const containerHeight = window.innerHeight || document.documentElement.clientHeight;

    entries
      .filter((entry) => entry.isIntersecting)
      .forEach((entry) => {
        const node = entry.target;
        const element = this.elements.get(node);

        // We wrap the child in a container, so we need to ensure we're looking at
        // the bounds of the child and not the wrapper
        const bounds = entry.boundingClientRect || node.getBoundingClientRect();

        const elementHeight = bounds.height;

        // If the element height is within x% (this.options.percentCompensation)
        // of the container height, use the errorMargin option (this.options.errorMargin).
        // Otherwise default to 0.25. The max intersectionRatio for a regular element
        // should be 0.75 as being _too_ strict can result in missed events.
        const errorMargin = this._isElementHeightSimilarToContainer(elementHeight, containerHeight)
          ? this.options.errorMargin
          : this.options.defaultMargin;

        const maxIntersectionRatio =
          containerHeight / elementHeight > 1 ? 1 - errorMargin : containerHeight / elementHeight - errorMargin;

        const isVisible = entry.intersectionRatio >= maxIntersectionRatio;
        // Element is still visible since last check
        if (isVisible && element && element.timeoutSet) {
          return;
        }

        // If the element is visible
        if (isVisible && element) {
          // Set the visibility state to true but wait until the timeout finishes
          // to fire the event
          element.timeoutSet = true;

          // Start timer
          this.timeouts.set(
            node,
            setTimeout(() => {
              // Fire the event
              this._onVisibilityChange(node, true);
            }, this.options.minTimeVisible),
          );
        } else {
          const timeout = this.timeouts.get(node);
          // Fire event
          this._onVisibilityChange(node, false);
          // Element is no longer visible, delete timeout
          clearTimeout(timeout);
          this.timeouts.delete(node);
          if (element) {
            element.timeoutSet = false;
          }
        }
      });
  };

  /**
   * Observe an IntersectionObserver entry
   * @param  {Element} element the element to watch
   * @param  {Function} callback the element callback event
   */
  observe = (element: Element, callback: (visible: boolean) => void): void => {
    this.elements.set(element, {
      callback,
      visible: false,
      entry: element,
    });

    this.observer.observe(element);
  };

  /**
   * Unobserve an IntersectionObserver entry
   * @param  {Element} element the element to unobserve
   */
  unobserve = (element: Element): void => {
    if (!this.elements.get(element)) {
      return;
    }

    this.observer.unobserve(element);

    this.elements.delete(element);
  };

  /**
   * callback for orientationchange or other event which may cause element visible change
   *
   * Fire onChange event listeners for all visible elements
   */
  private _reportVisibilityStates = (): void => {
    this.elements.forEach(({ visible }, element) => {
      if (visible) {
        this._onVisibilityChange(element, visible);
      }
    });
  };

  /**
   * Handle DOM element visibility state change
   * @param  {Element} element - the DOM node
   * @param  {Boolean} visible - the visibility state of the entry
   */
  private _onVisibilityChange(element: Element, visible: boolean): void {
    const entry = this.elements.get(element);

    if (!entry) {
      return;
    }

    // Set the new visibility state
    entry.visible = visible;

    const { callback } = entry;
    callback(visible);
  }

  private _isElementHeightSimilarToContainer(elementHeight: number, containerHeight: number): boolean {
    return (
      elementHeight >= containerHeight - containerHeight * this.options.percentCompensation &&
      elementHeight <= containerHeight + containerHeight * this.options.percentCompensation
    );
  }

  private _addEventListeners(): void {
    window.addEventListener('focus', this._reportVisibilityStates);
    window.addEventListener('orientationchange', this._reportVisibilityStates);
  }

  private _removeEventListeners(): void {
    window.removeEventListener('focus', this._reportVisibilityStates);
    window.removeEventListener('orientationchange', this._reportVisibilityStates);
  }
}
